import React, { useCallback, useMemo, useRef } from "react";
import classnames from "classnames";

import CompactSearch from "@vahak/core-ui/dist/components/CompactSearch";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { isMobileViewport } from "@vahak/core-ui/dist/methods/browser";
import { useKeyboardAwareField } from "@vahak/core-ui/dist/hooks/useKeyboardAwareField";
import { SelectOnChangeType, SelectOption } from "@vahak/core-ui/dist/components/Select";
import { LocationSearchParamsOption } from "@vahak/core/dist/_services/city-search.service";
import { PlaceSearchResult } from "@vahak/core/dist/custom-types/location-search";

import useHomepageV2Translation from "../../../hooks/useHomepageV2Translation";
import { getQuoteFormLocationFieldConfig, QuoteFormLocationFieldRole } from "../../../methods/homepageV2CompactSheet";
import { generateCityLoadOption } from "../../../../../methods/location-search";
import { generateSelectOption } from "../../../../../methods/uiUtil";
import { mapPlaceSearchResponseIntoText } from "../../../../posting-form/common/current-location/UserCurrentLocation";
import {
    createQuoteFormLocationSelectComponents,
    getQuoteFormLocationMenuConfig,
    getQuoteFormLocationNoOptionsMessage,
    QuoteFormLocationMessages
} from "../../../load-quote/quoteFormSelectParts";
import Styles from "../QuoteFormFields.module.scss";

export type { QuoteFormLocationMessages };

export type QuoteFormLocationFieldHandle = {
    focus: () => void;
};

interface QuoteFormLocationFieldProps {
    id: string;
    name: string;
    placeholder: string;
    icon: React.ReactNode;
    value?: PlaceSearchResult;
    onChange: (value?: PlaceSearchResult) => void;
    locationSearchSource: LocationSearchParamsOption["source"];
    isDisabled?: boolean;
    inSheet?: boolean;
    fieldRole?: QuoteFormLocationFieldRole;
    messages?: QuoteFormLocationMessages;
    onInputValueChange?: (value: string) => void;
    /** Portals the menu to document.body (avoids clipping in stacked hero fields / modals). */
    portalMenu?: boolean;
    selectRef?: React.Ref<QuoteFormLocationFieldHandle>;
}

const QuoteFormLocationField = ({
    id,
    name,
    placeholder,
    icon,
    value,
    onChange,
    locationSearchSource,
    isDisabled = false,
    inSheet = false,
    fieldRole = "pickup",
    messages,
    onInputValueChange,
    portalMenu = false,
    selectRef
}: QuoteFormLocationFieldProps) => {
    const fieldWrapRef = useRef<HTMLDivElement>(null);
    // Sheet fields mount on open. Defaulting the media query to `false` paints desktop
    // select config for a frame, then flips — that remount loses the first tap's focus.
    // Quote form is already on the page, so its query has settled before anyone taps.
    const isMobile = useMediaQuery({
        queryType: "medium-screen",
        initialState: inSheet ? isMobileViewport() : false
    });
    const fieldConfig = useMemo(() => {
        const base = getQuoteFormLocationFieldConfig({ inSheet, role: fieldRole, isMobile });

        if (!portalMenu) {
            return base;
        }

        return {
            ...base,
            portalMenu: true
        };
    }, [fieldRole, inSheet, isMobile, portalMenu]);
    const menuConfig = useMemo(
        () => getQuoteFormLocationMenuConfig(fieldConfig.portalMenu, inSheet),
        [fieldConfig.portalMenu, inSheet]
    );
    const { menuPlacement, onFieldFocus, onFieldBlur, syncPlacement } = useKeyboardAwareField(fieldWrapRef, {
        enabled: fieldConfig.keyboardAware,
        // Route-step fields sit at the top of the sheet. Scrolling the overflow root
        // on focus dismisses the keyboard on some iOS / WebView builds.
        scrollOnFocus: !inSheet,
        menuMaxHeight: fieldConfig.maxMenuHeight,
        defaultMenuPlacement: fieldConfig.menuPlacement
    });
    const { t } = useHomepageV2Translation();
    const copy: QuoteFormLocationMessages = messages ?? {
        enterLocation: t("homepageV2:hero.quoteForm.enterLocation"),
        noSuchLocation: t("homepageV2:hero.quoteForm.noSuchLocation"),
        getMoreCharactersLabel: (count) => t("homepageV2:hero.quoteForm.moreCharacters", { count })
    };

    const selectedOption = value
        ? generateSelectOption(mapPlaceSearchResponseIntoText(value, 2), value, value.place_id)
        : undefined;

    const handleChange: SelectOnChangeType = (option) => {
        const selected = option as SelectOption<PlaceSearchResult> | null;
        onChange(selected?.value);
    };

    const getNoOptionsMessage = useCallback(
        (inputValue: string) => getQuoteFormLocationNoOptionsMessage(inputValue, copy),
        [copy]
    );

    const selectComponents = useMemo(
        () =>
            createQuoteFormLocationSelectComponents(Styles, {
                icon: icon as React.ReactNode,
                getNoOptionsMessage,
                revealNativeInput: inSheet
            }),
        [getNoOptionsMessage, icon, inSheet]
    );

    return (
        <div
            ref={fieldWrapRef}
            className={classnames(Styles.fieldWrap, Styles.fieldWrapSelect, Styles.fieldWrapSearchable)}
        >
            <CompactSearch
                ref={selectRef}
                maxMenuHeight={fieldConfig.maxMenuHeight}
                id={id}
                name={name}
                option={[]}
                value={selectedOption}
                placeholder={placeholder}
                isClearable
                isDisabled={isDisabled}
                isSearchable
                openMenuOnClick={false}
                customDropDownIcon={<></>}
                menuPlacement={menuPlacement}
                menuShouldScrollIntoView={fieldConfig.menuShouldScrollIntoView}
                onFocus={onFieldFocus}
                onBlur={onFieldBlur}
                onMenuOpen={syncPlacement}
                {...menuConfig}
                loadOptions={(str, cb) =>
                    generateCityLoadOption(str, cb, {
                        source: locationSearchSource
                    })
                }
                onChange={handleChange}
                onInputChange={(value: string) => {
                    if (value) {
                        syncPlacement();
                    }
                    onInputValueChange?.(value);
                }}
                noOptionHandler={(o) => getNoOptionsMessage(o.inputValue)}
                components={selectComponents}
            />
        </div>
    );
};

export default QuoteFormLocationField;
